// /home/labdoo/zitech-nexus/frontend/src/pages/admin/portfolio/PortfolioSettings.tsx
import React, { useState, useEffect } from 'react';
import { portfolioAPI } from '../../../services/api';
import './PortfolioAdmin.css';

// SVG Icons
const Icons = {
  Save: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  Spinner: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pa-spinner-small">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Check: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round">
      <path d="M20 6L9 17L4 12" />
    </svg>
  ),
  Settings: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
};

interface Settings {
  section_title: string;
  section_subtitle: string;
  projects_per_page: number;
  solutions_per_page: number;
  show_featured_only: boolean;
  show_cta: boolean;
  cta_title: string;
  cta_button_text: string;
  cta_button_link: string;
}

const PortfolioSettings: React.FC = () => {
  const [settings, setSettings] = useState<Settings>({
    section_title: 'Our Portfolio & Solutions',
    section_subtitle: 'Explore our successful projects and innovative solutions that drive business growth',
    projects_per_page: 6,
    solutions_per_page: 6,
    show_featured_only: false,
    show_cta: true,
    cta_title: 'Ready to Start Your Project?',
    cta_button_text: 'Contact Us',
    cta_button_link: '/contact',
  });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await portfolioAPI.getSettings();
      setSettings(response.data);
    } catch (err: any) {
      console.error('Error fetching settings:', err);
      setError('Failed to load settings');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pa-form-container">
      {/* Header */}
      <div className="pa-form-header">
        <div className="pa-form-header-left">
          <h1 className="pa-form-title">
            <Icons.Settings />
            Portfolio Settings
          </h1>
          <p className="pa-subtitle">Configure your portfolio display and preferences</p>
        </div>
      </div>

      {saved && (
        <div className="pa-success">
          <Icons.Check />
          <span>Settings saved successfully!</span>
        </div>
      )}
      {error && <div className="pa-error">{error}</div>}

      {/* Form */}
      <form onSubmit={handleSubmit} className="pa-form">
        <div className="pa-form-grid">
          {/* Left Column */}
          <div className="pa-form-left">
            <div className="pa-card">
              <h3 className="pa-card-title">Section Header</h3>
              
              <div className="pa-form-group">
                <label className="pa-form-label">Section Title</label>
                <input
                  type="text"
                  name="section_title"
                  className="pa-form-input"
                  value={settings.section_title}
                  onChange={handleChange}
                />
              </div>

              <div className="pa-form-group">
                <label className="pa-form-label">Section Subtitle</label>
                <textarea
                  name="section_subtitle"
                  className="pa-form-textarea"
                  value={settings.section_subtitle}
                  onChange={handleChange}
                  rows={3}
                />
              </div>
            </div>

            <div className="pa-card">
              <h3 className="pa-card-title">Call to Action</h3>
              
              <div className="pa-form-group pa-form-checkbox">
                <label className="pa-checkbox-label">
                  <input
                    type="checkbox"
                    name="show_cta"
                    checked={settings.show_cta}
                    onChange={handleChange}
                  />
                  <span className="pa-checkbox-text">Show Call to Action Section</span>
                </label>
              </div>

              <div className="pa-form-group">
                <label className="pa-form-label">CTA Title</label>
                <input
                  type="text"
                  name="cta_title"
                  className="pa-form-input"
                  value={settings.cta_title}
                  onChange={handleChange}
                />
              </div>

              <div className="pa-form-row">
                <div className="pa-form-group">
                  <label className="pa-form-label">Button Text</label>
                  <input
                    type="text"
                    name="cta_button_text"
                    className="pa-form-input"
                    value={settings.cta_button_text}
                    onChange={handleChange}
                  />
                </div>

                <div className="pa-form-group">
                  <label className="pa-form-label">Button Link</label>
                  <input
                    type="text"
                    name="cta_button_link"
                    className="pa-form-input"
                    value={settings.cta_button_link}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="pa-form-right">
            <div className="pa-card">
              <h3 className="pa-card-title">Display Settings</h3>
              
              <div className="pa-form-row">
                <div className="pa-form-group">
                  <label className="pa-form-label">Projects Per Page</label>
                  <input
                    type="number"
                    name="projects_per_page"
                    className="pa-form-input"
                    value={settings.projects_per_page}
                    onChange={handleChange}
                    min="1"
                    max="50"
                  />
                </div>

                <div className="pa-form-group">
                  <label className="pa-form-label">Solutions Per Page</label>
                  <input
                    type="number"
                    name="solutions_per_page"
                    className="pa-form-input"
                    value={settings.solutions_per_page}
                    onChange={handleChange}
                    min="1"
                    max="50"
                  />
                </div>
              </div>

              <div className="pa-form-group pa-form-checkbox">
                <label className="pa-checkbox-label">
                  <input
                    type="checkbox"
                    name="show_featured_only"
                    checked={settings.show_featured_only}
                    onChange={handleChange}
                  />
                  <span className="pa-checkbox-text">Show Featured Only (on homepage)</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="pa-form-actions">
          <button type="submit" className="pa-btn-primary" disabled={loading}>
            {loading ? (
              <>
                <Icons.Spinner />
                Saving...
              </>
            ) : (
              <>
                <Icons.Save />
                Save Settings
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PortfolioSettings;